import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import EditorToolbar, { FormatType } from './EditorToolbar';
import SectionEditor, { SectionEditorHandle } from './SectionEditor';
import { REPORT_SECTIONS } from '../LeftPanel/OutlineSection';
import MarkdownWithAssets from '../../../../../components/report/MarkdownWithAssets';
import useChartData from '../../../../../hooks/useChartData';
import './MiddlePanel.css';

// Markdown syntax mappings - placeholders are resolved at render time via i18n
const getMarkdownSyntax = (t: (key: string) => string): Record<FormatType, { prefix: string; suffix: string; placeholder: string }> => ({
  bold: { prefix: '**', suffix: '**', placeholder: t('editorContent.boldText') },
  italic: { prefix: '*', suffix: '*', placeholder: t('editorContent.italicText') },
  underline: { prefix: '<u>', suffix: '</u>', placeholder: t('editorContent.underlinedText') },
  bulletList: { prefix: '\n- ', suffix: '', placeholder: t('editorContent.listItem') },
  numberedList: { prefix: '\n1. ', suffix: '', placeholder: t('editorContent.listItem') },
});

interface MiddlePanelProps {
  activeSection: string;
  content: string;
  onChange: (content: string) => void;
  isReadOnly?: boolean;
  institutionId?: string;
  period?: string;
  isPreviewMode: boolean;
  onTogglePreview: () => void;
}

export default function MiddlePanel({
  activeSection,
  content,
  onChange,
  isReadOnly = false,
  institutionId = '',
  period = '',
  isPreviewMode,
  onTogglePreview,
}: MiddlePanelProps) {
  const { t } = useTranslation();
  const editorRef = useRef<SectionEditorHandle>(null);

  // Fetch chart data for rendering embedded charts/tables
  const { data: chartData, isLoading: isChartDataLoading } = useChartData(institutionId, period);

  // Find the section metadata
  const sectionMeta = REPORT_SECTIONS.find((s) => s.id === activeSection);

  // Handle format button clicks
  const handleFormat = (type: FormatType) => {
    const markdownSyntax = getMarkdownSyntax(t);
    const syntax = markdownSyntax[type];
    editorRef.current?.insertText(syntax.prefix, syntax.suffix, syntax.placeholder);
  };

  return (
    <div className="middle-panel">
      {/* Section Header */}
      <div className="section-header-area">
        <span className="section-number">{sectionMeta?.num || '00'}</span>
        <h2 className="section-title">{sectionMeta?.title || t('editorContent.unknownSection')}</h2>
      </div>

      {/* Editor Toolbar */}
      <EditorToolbar
        onFormat={handleFormat}
        isPreviewMode={isPreviewMode}
        onTogglePreview={onTogglePreview}
        isReadOnly={isReadOnly}
      />

      {/* Section Editor or Preview */}
      {isPreviewMode ? (
        <div className="editor-preview">
          <MarkdownWithAssets
            content={content}
            chartData={chartData}
            isLoading={isChartDataLoading}
          />
        </div>
      ) : (
        <SectionEditor
          ref={editorRef}
          content={content}
          onChange={onChange}
          isReadOnly={isReadOnly}
          placeholder={t('editorContent.writePlaceholder', { section: sectionMeta?.title || t('editorContent.unknownSection') })}
        />
      )}
    </div>
  );
}
